import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import type { Theme } from "@material-ui/core/styles";
import { makeStyles } from "@material-ui/core/styles";
import MuiTable from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import { format } from "date-fns";
import ja from "date-fns/locale/ja";
import { SignupParamItem } from "../../../models/signupParam";

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    overflow: "auto",
    padding: 0,
  },
  inner: {
    minWidth: 1510,
  },
  table: {
    tableLayout: "fixed",
    width: "100%",
  },
  customerCell: {
    width: 180,
  },
  uidCell: {
    width: 200,
  },
  timeCell: {
    width: 130,
  },
  utmCell: {
    width: 110,
  },
  otherCell: {
    width: 160,
  },
  uidText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  emailText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  utmText: {
    wordBreak: "break-all",
  },
  otherDetails: {
    cursor: "pointer",
  },
  otherPre: {
    fontSize: 11,
    background: theme.palette.grey[100],
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    borderRadius: 4,
    overflow: "auto",
    maxWidth: 280,
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-all" as const,
  },
}));

function formatName(item: SignupParamItem): string {
  const name = [item.lastName, item.firstName].filter(Boolean).join(" ");
  return name || "-";
}

function formatGender(gender?: string): string {
  if (gender === "male") return "男";
  if (gender === "female") return "女";
  return "";
}

function formatAge(birthYear?: number): string {
  if (!birthYear) return "";
  const age = new Date().getFullYear() - birthYear;
  if (age <= 0) return "";
  return `${age}歳`;
}

function formatProfileMeta(item: SignupParamItem): string {
  const prefecture = item.prefecture || "";
  const age = formatAge(item.birthYear);
  const gender = formatGender(item.gender);
  const ageAndGender =
    age && gender ? `${age}(${gender})` : age || (gender ? `(${gender})` : "");
  return [prefecture, ageAndGender].filter(Boolean).join("・") || "-";
}

type ParsedParams = {
  utm_campaign?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_term?: string;
  placement?: string;
  utm_content?: string;
  [key: string]: unknown;
};

function parseParams(params: string): ParsedParams | null {
  if (!params || params.trim() === "") return null;

  try {
    const parsed = JSON.parse(params);
    if (typeof parsed === "object" && parsed !== null) return parsed;
  } catch {
    // not JSON
  }

  try {
    const queryString = params.startsWith("?") ? params.slice(1) : params;
    const searchParams = new URLSearchParams(queryString);
    const result: ParsedParams = {};
    searchParams.forEach((value, key) => {
      result[key] = value.trim();
    });
    return Object.keys(result).length > 0 ? result : null;
  } catch {
    return null;
  }
}

function getOtherParams(
  parsed: ParsedParams
): Record<string, unknown> {
  const {
    utm_campaign,
    utm_medium,
    utm_source,
    utm_term,
    placement,
    utm_content,
    ...rest
  } = parsed;
  return rest;
}

export default function Table({
  className,
  signupParams,
}: {
  className?: string;
  signupParams: SignupParamItem[];
}) {
  const classes = useStyles();

  return (
    <Card className={className}>
      <CardContent className={classes.content}>
        <div className={classes.inner}>
          <MuiTable className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.customerCell}>顧客</TableCell>
                <TableCell className={classes.uidCell}>
                  UID
                  <br />
                  メールアドレス
                </TableCell>
                <TableCell className={classes.timeCell}>登録日時</TableCell>
                <TableCell className={classes.utmCell}>campaign</TableCell>
                <TableCell className={classes.utmCell}>medium</TableCell>
                <TableCell className={classes.utmCell}>source</TableCell>
                <TableCell className={classes.utmCell}>term</TableCell>
                <TableCell className={classes.utmCell}>placement</TableCell>
                <TableCell className={classes.utmCell}>content</TableCell>
                <TableCell className={classes.otherCell}>その他</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signupParams.length > 0 ? (
                signupParams.map((item) => {
                  const parsed = parseParams(item.params);
                  const otherParams = parsed ? getOtherParams(parsed) : null;
                  const hasOtherParams =
                    otherParams !== null && Object.keys(otherParams).length > 0;

                  return (
                    <TableRow hover key={item.uid}>
                      <TableCell className={classes.customerCell}>
                        <Typography noWrap>{formatName(item)}</Typography>
                        <Typography color="textSecondary" variant="body2" noWrap>
                          ({item.nickname || "-"})
                        </Typography>
                        <Typography color="textSecondary" variant="body2" noWrap>
                          {formatProfileMeta(item)}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.uidCell}>
                        <Typography
                          className={classes.uidText}
                          variant="body2"
                          noWrap
                        >
                          {item.uid}
                        </Typography>
                        <Typography
                          className={classes.emailText}
                          color="textSecondary"
                          variant="body2"
                          noWrap
                        >
                          {item.email || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.timeCell}>
                        <Typography variant="body2" noWrap>
                          {format(item.createdAt, "yyyy-MM-dd HH:mm", {
                            locale: ja,
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.utmCell}>
                        <Typography
                          className={classes.utmText}
                          variant="body2"
                          title={String(parsed?.utm_campaign || "")}
                        >
                          {String(parsed?.utm_campaign || "-")}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.utmCell}>
                        <Typography
                          className={classes.utmText}
                          variant="body2"
                          title={String(parsed?.utm_medium || "")}
                        >
                          {String(parsed?.utm_medium || "-")}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.utmCell}>
                        <Typography
                          className={classes.utmText}
                          variant="body2"
                          title={String(parsed?.utm_source || "")}
                        >
                          {String(parsed?.utm_source || "-")}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.utmCell}>
                        <Typography
                          className={classes.utmText}
                          variant="body2"
                          title={String(parsed?.utm_term || "")}
                        >
                          {String(parsed?.utm_term || "-")}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.utmCell}>
                        <Typography
                          className={classes.utmText}
                          variant="body2"
                          title={String(parsed?.placement || "")}
                        >
                          {String(parsed?.placement || "-")}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.utmCell}>
                        <Typography
                          className={classes.utmText}
                          variant="body2"
                          title={String(parsed?.utm_content || "")}
                        >
                          {String(parsed?.utm_content || "-")}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.otherCell}>
                        <details className={classes.otherDetails}>
                          <summary>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              component="span"
                            >
                              {hasOtherParams
                                ? `${Object.keys(otherParams!).length}件`
                                : item.params
                                  ? "生データ"
                                  : "-"}
                            </Typography>
                          </summary>
                          <pre className={classes.otherPre}>
                            {hasOtherParams
                              ? JSON.stringify(otherParams!, null, 2)
                              : item.params || "(空)"}
                          </pre>
                        </details>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography color="textSecondary" style={{ padding: 32 }}>
                      該当するデータがありません
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </MuiTable>
        </div>
      </CardContent>
    </Card>
  );
}
